import React, { useRef } from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import FilePondPluginFileEncode from 'filepond-plugin-file-encode';
import FilePondPluginImageTransform from 'filepond-plugin-image-transform';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImageResize from 'filepond-plugin-image-resize';
import FilePondPluginImageCrop from 'filepond-plugin-image-crop';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import typography from 'assets/theme/base/typography';
import rgba from 'assets/theme/function/rgba';

interface Props {
    setOpenImgDialog: (open: boolean) => void;
    imageUrl?: any;
    onSubmitFun: (image: any) => void;
    dialogTitle: string;
}

registerPlugin(
    FilePondPluginImageExifOrientation,
    FilePondPluginImagePreview,
    FilePondPluginFileEncode,
    FilePondPluginFileValidateSize,
    FilePondPluginFileValidateType,
    FilePondPluginImageResize,
    FilePondPluginImageCrop,
    FilePondPluginImageTransform,
);

const AddImages: React.FC<Props> = ({
    imageUrl,
    onSubmitFun,
    setOpenImgDialog,
    dialogTitle,
}) => {
    const pondRef = useRef<any>(null);
    const selectedFileRef = useRef<File | null>(null);
    const skipNextUploadTrigger = useRef(false);

    const closeModal = () => {
        setOpenImgDialog(false);
    };

    const handleUploadClick = () => {
        if (pondRef.current) {
            setTimeout(() => {
                pondRef.current.browse();
            }, 0);
        }
    };

    return (
        <>
            <Box
                display="flex"
                justifyContent="center"
                bgcolor={rgba(colors.primary.main, 0.1)}
            >
                <DialogTitle
                    fontSize={typography.size?.xxxxl}
                    fontWeight={typography.fontWeightXBold}
                    textTransform="uppercase"
                >
                    {dialogTitle}
                </DialogTitle>
                <IconButton
                    onClick={closeModal}
                    aria-label="close"
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent>
                <Box
                    sx={{
                        '& .filepond--root': {
                            width: '100%',
                            backgroundColor: 'transparent',
                        },
                        '& .filepond--panel-root': {
                            backgroundColor: 'transparent',
                        },
                        '& .filepond--drop-label': {
                            minHeight: '250px',
                        },
                        '& .filepond--list': {
                            display: 'flex',
                            justifyContent: 'center',
                        },
                        '& .filepond--item-panel': {
                            backgroundColor: 'transparent',
                        },
                        '& .filepond--file': {
                            width: '100%',
                            maxWidth: '100%',
                        },
                        '& .filepond--image-preview': {
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                        },
                        '& .filepond--credits': {
                            display: 'none',
                        },
                    }}
                >
                    <FilePond
                        ref={pondRef}
                        files={
                            imageUrl && imageUrl.some(Boolean)
                                ? imageUrl
                                : imageUrl.filter(Boolean).map((url: any) => ({
                                      source: url,
                                      options: { type: 'local' },
                                  }))
                        }
                        required
                        acceptedFileTypes={['application/pdf', 'image/*']}
                        allowFileEncode
                        allowImageTransform
                        allowImagePreview
                        allowImageCrop={false}
                        allowImageResize={false}
                        server={null}
                        name="files"
                        labelIdle={'Upload Image'}
                        onupdatefiles={(fileItems) => {
                            if (fileItems.length > 0) {
                                selectedFileRef.current = fileItems[0]
                                    .file as File;
                            } else {
                                selectedFileRef.current = null;
                                if (!skipNextUploadTrigger.current) {
                                    handleUploadClick();
                                }
                                skipNextUploadTrigger.current = false;
                            }
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        if (selectedFileRef.current) {
                            skipNextUploadTrigger.current = true;
                            const formData = new FormData();
                            formData.append('image', selectedFileRef.current);
                            onSubmitFun(formData);
                        }
                    }}
                >
                    Submit
                </Button>
            </DialogActions>
        </>
    );
};

export default AddImages;
